import RxRegister from "@/components/reports/RxRegister";
import { MEDICAL_SHOP } from "@/lib/catalog";
import ZakatPanel from "@/components/reports/ZakatPanel";
import SatisfactionPanel from "@/components/reports/SatisfactionPanel";
import { useMemo, useState } from "react";
import { Banknote, NotebookPen, HandCoins, Wallet, AlertTriangle, TrendingUp, FileDown, Home } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useApp } from "@/context/AppContext";
import { fmtNum, isLow, vals } from "@/lib/format";
import { Chip, PageTitle, Stat } from "@/components/ui-kit";
import { usePdfReport } from "@/components/PdfReport";
import { buildReport } from "@/lib/reports";

const PDFS = ["sales", "top", "pnl", "stock", "low", "expiry", "debts", "purchases", "expenses", "waste"];

function PdfPanel({ days }) {
  const { view, t, lang, currency } = useApp();
  const pdf = usePdfReport();
  return (
    <div className="rounded-2xl bg-white border border-border p-4 card-soft" data-testid="pdf-reports-panel">
      <h3 className="font-heading text-lg mb-3">{t("pdf_reports")}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {PDFS.map((k) => (
          <button key={k} type="button" disabled={pdf.busy} onClick={() => pdf.exportReport(buildReport(k, { view, t, lang, currency, days }))} data-testid={`pdf-report-${k}`}
            className="min-h-[56px] px-3 rounded-xl border border-border bg-background hover:border-primary text-start font-semibold flex items-center gap-2 disabled:opacity-50">
            <FileDown className="w-5 h-5 text-primary shrink-0" />{t(`pdf_${k}`)}
          </button>
        ))}
      </div>
      {pdf.node}
    </div>
  );
}

const RANGES = [1, 7, 30];
const dayKey = (iso) => iso.slice(0, 10);

function useReport(days) {
  const { view } = useApp();
  return useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    start.setDate(start.getDate() - (days - 1));
    const from = start.toISOString();
    const sales = vals(view.sales).filter((s) => s.created_at >= from);
    const payments = vals(view.payments).filter((p) => p.created_at >= from);
    const purchases = vals(view.purchases).filter((p) => p.created_at >= from);
    const byDay = {};
    for (let i = 0; i < days; i += 1) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      byDay[d.toISOString().slice(0, 10)] = 0;
    }
    const top = {};
    let sold = 0;
    sales.forEach((s) => {
      const k = dayKey(new Date(s.created_at).toISOString());
      if (k in byDay) byDay[k] += s.total;
      s.items.forEach((i) => {
        top[i.name] = top[i.name] || { name: i.name, qty: 0, revenue: 0 };
        top[i.name].qty += i.qty;
        top[i.name].revenue += i.qty * i.price;
        sold += i.qty;
      });
    });
    const sum = (arr, f) => arr.reduce((a, x) => a + (f(x) || 0), 0);
    const customers = vals(view.customers);
    return {
      total: sum(sales, (s) => s.total),
      topups: ["mobilis", "djezzy", "ooredoo"].map((k) => { const lines = sales.flatMap((s) => (s.items || []).filter((i) => i.topup?.operator === k)); return { k, n: lines.length, v: sum(lines, (i) => i.price * i.qty) }; }),
      count: sales.length,
      cash: sum(sales, (s) => (["house", "card", "transfer"].includes(s.payment) ? 0 : s.paid)),
      credit: sum(sales, (s) => (s.payment === "credit" ? s.total - (s.paid || 0) : 0)),
      house: sum(sales, (s) => (s.payment === "house" ? sum(s.items || [], (i) => (i.cost ?? view.products[i.product_id]?.cost ?? 0) * i.qty) : 0)),
      repaid: sum(payments, (p) => (p.kind === "refund" ? -p.amount : p.amount)),
      debt: sum(customers, (c) => Math.max(c.balance, 0)),
      debtors: customers.filter((c) => c.balance > 0).sort((a, b) => b.balance - a.balance).slice(0, 5),
      low: vals(view.products).filter((p) => !p.deleted && isLow(p, view.shop?.low_stock_default)).length,
      chart: Object.entries(byDay).map(([d, v]) => ({ d: d.slice(5), v })),
      top: Object.values(top).sort((a, b) => b.qty - a.qty).slice(0, 5),
      sold,
      bought: purchases.reduce((a, p) => a + p.items.reduce((x, i) => x + i.qty, 0), 0),
    };
  }, [view, days]);
}

export default function Reports() {
  const { t, money, view } = useApp();
  const [days, setDays] = useState(7);
  const r = useReport(days);
  return (
    <div className="space-y-5">
      <PageTitle testid="reports-title">{t("nav_reports")}</PageTitle>
      <div className="flex gap-2">{RANGES.map((d) => <Chip key={d} active={days === d} onClick={() => setDays(d)} data-testid={`reports-range-${d}`}>{t(`range_${d}`)}</Chip>)}</div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label={t("sales")} value={money(r.total)} icon={TrendingUp} tone="#0B2A5B" testid="report-sales-total" />
        <Stat label={t("cash_in")} value={money(r.cash)} icon={Banknote} tone="#1F9D55" testid="report-cash-total" />
        <Stat label={t("new_credit")} value={money(r.credit)} icon={NotebookPen} tone="#E67E22" testid="report-credit-total" />
        <Stat label={t("house_cost")} value={money(r.house)} icon={Home} tone="#7F9BC0" testid="report-house-total" />
        <Stat label={t("repayments")} value={money(r.repaid)} icon={HandCoins} tone="#1F9D55" testid="report-repaid-total" />
        <Stat label={t("total_debt")} value={money(r.debt)} icon={Wallet} tone="#E74C3C" testid="report-debt-total" />
        <Stat label={t("low_stock")} value={r.low} icon={AlertTriangle} tone="#E74C3C" testid="report-low-count" />
        <Stat label={t("units_sold")} value={fmtNum(r.sold)} testid="report-units-sold" />
        <Stat label={t("units_bought")} value={fmtNum(r.bought)} testid="report-units-bought" />
      </div>
      <PdfPanel days={days} />
      <ZakatPanel />
      <SatisfactionPanel />
      {MEDICAL_SHOP(view.shop?.type) && <RxRegister />}
      {view.shop?.type === "phones" && (
        <div className="rounded-2xl bg-white border border-border p-4" data-testid="report-topups">
          <h3 className="font-heading font-bold text-lg mb-2">{t("topups_by_operator")}</h3>
          <div className="grid grid-cols-3 gap-3">{r.topups.map((o) => <div key={o.k} className="rounded-xl border border-border p-3" data-testid={`report-topup-${o.k}`}><div className="font-black capitalize">{o.k}</div><div className="text-xl font-bold" dir="ltr">{money(o.v)}</div><div className="text-xs text-muted-foreground">{o.n} {t("records")}</div></div>)}</div>
        </div>
      )}
      <div className="hidden">
      </div>
      <div className="rounded-2xl bg-white border border-border p-4" data-testid="report-sales-chart">
        <h3 className="font-heading font-bold text-lg mb-2">{t("sales_by_day")}</h3>
        <div className="h-56" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={r.chart}><XAxis dataKey="d" fontSize={11} /><YAxis fontSize={11} width={48} /><Tooltip formatter={(v) => fmtNum(v)} /><Bar dataKey="v" fill="#0B2A5B" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-border p-4" data-testid="report-top-products">
          <h3 className="font-heading font-bold text-lg mb-2">{t("top_sellers")}</h3>
          {r.top.length === 0 && <p className="text-muted-foreground">{t("no_history")}</p>}
          {r.top.map((p, i) => (
            <div key={p.name} className="flex items-center gap-3 py-2 border-b border-border last:border-0">
              <span className="w-8 h-8 rounded-full bg-secondary grid place-items-center font-black">{i + 1}</span>
              <span className="flex-1 font-bold truncate">{p.name}</span>
              <span className="text-sm">{fmtNum(p.qty)} · {money(p.revenue)}</span>
            </div>
          ))}
        </div>
        <div className="rounded-2xl bg-white border border-border p-4" data-testid="report-top-debtors">
          <h3 className="font-heading font-bold text-lg mb-2">{t("top_debtors")}</h3>
          {r.debtors.length === 0 && <p className="text-muted-foreground">{t("all_good")}</p>}
          {r.debtors.map((c) => (
            <div key={c.id} className="flex justify-between py-2 border-b border-border last:border-0"><span className="font-bold">{c.name} <span className="text-muted-foreground text-sm">{c.family}</span></span><span className="font-extrabold text-[#E67E22]">{money(c.balance)}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}
